import React, { useEffect, useRef } from "react";
import { Box } from "../design-system";
import { colors } from "../design-system/colors";
import { CaptionStyle, Orientation, REEL, ViewSize } from "./constants/reelText";
import { ReelClip } from "./hooks/useShortlist";
import VerticalPreview, { ChosenFrame } from "./VerticalPreview";

interface ReelClipRowProps {
  clip: ReelClip;
  videoUrl: string;
  /** Only the active row mounts a <video>; the rest show their poster. */
  isActive: boolean;
  onPlay: () => void;
  isSelected: boolean;
  onToggle: () => void;
  /** Small = the compact list; Large = posters twice the size, to judge the picture. */
  size?: ViewSize;
  /** For the 9:16 preview beside the poster. */
  captions?: boolean;
  captionStyle?: CaptionStyle;
  /** The shape this clip exports as; the chosen frame glows once the clip is ticked. */
  shape?: Orientation;
  onChooseShape?: (shape: Orientation) => void;
}

// Poster width per view size; each 9:16 preview (Blur, Zoom) is half as wide (so a bit taller).
const POSTER_WIDTH: Record<ViewSize, number> = { small: 180, large: 400 };

const formatSeconds = (seconds: number) => {
  const whole = Math.max(0, Math.round(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

/**
 * One clip: poster on the left, the words on the right — the words are the product, so
 * they get the space. A wall of thumbnails would hide the thing CherryClipz is for.
 *
 * Playback SEEKS the source video rather than loading a cut file, the same way the ET
 * does, which is why this page can exist without rendering anything.
 */
const ReelClipRow = ({
  clip,
  videoUrl,
  isActive,
  onPlay,
  isSelected,
  onToggle,
  size = "small",
  captions = true,
  captionStyle = "cherry",
  shape = "original",
  onChooseShape,
}: ReelClipRowProps) => {
  const posterWidth = POSTER_WIDTH[size];
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Seek to the clip's start once metadata is in, then stop at its end. Keyed by clip id
  // (never a row index) — a re-sorted list otherwise plays the wrong clip forever.
  useEffect(() => {
    if (!isActive) return;
    const video = videoRef.current;
    if (!video) return;

    const seek = () => {
      video.currentTime = clip.start_in;
      video.play().catch(() => {
        /* autoplay refused — the user can press play on the control */
      });
    };
    const stopAtEnd = () => {
      if (video.currentTime >= clip.end_in) video.pause();
    };

    video.addEventListener("loadedmetadata", seek);
    video.addEventListener("timeupdate", stopAtEnd);
    if (video.readyState >= 1) seek();

    return () => {
      video.removeEventListener("loadedmetadata", seek);
      video.removeEventListener("timeupdate", stopAtEnd);
    };
  }, [isActive, clip.start_in, clip.end_in]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "16px",
        padding: "12px",
        borderRadius: "12px",
        alignItems: "flex-start",
        backgroundColor: isSelected ? "rgba(233, 30, 99, 0.10)" : colors.card.contentBackground,
        border: `1px solid ${isSelected ? colors.primary[500] : "transparent"}`,
        flexWrap: "wrap",
      }}
    >
      <ChosenFrame chosen={isSelected && shape === "original"}>
        <Box
          title={REEL.PICK_ORIGINAL}
          sx={{
            position: "relative",
            width: `${posterWidth}px`,
            maxWidth: "100%",
            minWidth: "140px",
            flex: "0 0 auto",
            aspectRatio: "16 / 9",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "rgba(0,0,0,0.35)",
            cursor: "pointer",
          }}
          onClick={() => {
            onPlay();
            // On a ticked clip, the poster is also the "original shape" choice.
            if (isSelected && shape !== "original") onChooseShape?.("original");
          }}
        >
          {isActive ? (
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <>
              {clip.thumbnail ? (
                <img
                  src={clip.thumbnail}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : null}
              <Box
                aria-label={REEL.PLAY}
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "32px",
                  color: colors.neutral.white,
                  textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                }}
              >
                ▶
              </Box>
            </>
          )}
        </Box>
        <Box
          component="span"
          aria-hidden
          sx={{
            display: "block",
            textAlign: "center",
            marginTop: "4px",
            fontFamily: "Outfit, sans-serif",
            fontSize: "12px",
            color: isSelected && shape === "original" ? "#FF6B8B" : "rgba(255,255,255,0.6)",
          }}
        >
          {REEL.LOOK_ORIGINAL}
        </Box>
      </ChosenFrame>

      <VerticalPreview
        thumbnail={clip.thumbnail}
        text={clip.text}
        width={Math.round(posterWidth / 2)}
        captions={captions}
        captionStyle={captionStyle}
        chosen={isSelected && shape === "vertical"}
        onClick={
          onChooseShape
            ? () => onChooseShape(isSelected && shape === "vertical" ? "original" : "vertical")
            : undefined
        }
      />

      <VerticalPreview
        look="zoom"
        thumbnail={clip.thumbnail}
        text={clip.text}
        width={Math.round(posterWidth / 2)}
        captions={captions}
        captionStyle={captionStyle}
        chosen={isSelected && shape === "vertical_fill"}
        onClick={
          onChooseShape
            ? () =>
                onChooseShape(isSelected && shape === "vertical_fill" ? "original" : "vertical_fill")
            : undefined
        }
      />

      <Box sx={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: "8px" }}>
        <Box
          component="p"
          sx={{
            margin: 0,
            fontFamily: "Outfit, sans-serif",
            fontSize: "16px",
            lineHeight: "24px",
            color: colors.neutral.white,
          }}
        >
          {clip.text || "—"}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            fontFamily: "Outfit, sans-serif",
            fontSize: "13px",
            color: colors.neutral[700],
          }}
        >
          <span>{formatSeconds(clip.seconds)}</span>
          {clip.speaker ? <span>· {clip.speaker}</span> : null}
          {clip.label ? <span>· {clip.label}</span> : null}
        </Box>
      </Box>

      <Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          alignSelf: "center",
          fontFamily: "Outfit, sans-serif",
          fontSize: "14px",
          color: colors.neutral[600],
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          style={{ width: "18px", height: "18px", accentColor: colors.primary[500] }}
        />
        Pick
      </Box>
    </Box>
  );
};

export default ReelClipRow;
